import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import type { AABB } from '../engine/Physics';
import { aabbOverlap } from '../engine/Physics';

export interface FlagDef {
  x: number;
  y: number;
  team: 'red' | 'blue';
}

export interface FlagState {
  x: number;
  y: number;
  baseX: number;
  baseY: number;
  pickupX: number; // место где подобрали
  pickupY: number;
  team: 'red' | 'blue';
  carriedBy: 'player' | null;
  onBase: boolean;
  gfx: Container;
}

const FLAG_W = 32;
const FLAG_H = 48;
const FLAG_COLORS = { red: 0xee3333, blue: 0x3366ee };

export class FlagSystem {
  flags: FlagState[] = [];
  private layer: Container;
  private flagRedUrl: string;
  private flagBlueUrl: string;

  // счёт
  scoreRed = 0;
  scoreBlue = 0;
  readonly scoreToWin = 3;

  constructor(layer: Container, flagRedUrl = '', flagBlueUrl = '') {
    this.layer = layer;
    this.flagRedUrl = flagRedUrl;
    this.flagBlueUrl = flagBlueUrl;
  }

  init(defs: FlagDef[]): void {
    // очищаем
    for (const f of this.flags) { f.gfx.destroy(); }
    this.flags = [];
    this.scoreRed = 0;
    this.scoreBlue = 0;

    for (const def of defs) {
      const gfx = new Container();
      this.drawFlag(gfx, def.team);
      gfx.x = def.x;
      gfx.y = def.y - FLAG_H;
      this.layer.addChild(gfx);

      this.flags.push({
        x: def.x,
        y: def.y - FLAG_H,
        baseX: def.x,
        baseY: def.y - FLAG_H,
        pickupX: def.x,
        pickupY: def.y - FLAG_H,
        team: def.team,
        carriedBy: null,
        onBase: true,
        gfx,
      });
    }
  }

  private drawFlag(gfx: Container, team: 'red' | 'blue'): void {
    gfx.removeChildren();
    const url = team === 'red' ? this.flagRedUrl : this.flagBlueUrl;
    if (url) {
      const tex = Texture.from(url);
      const sprite = new Sprite(tex);
      sprite.width = FLAG_W;
      sprite.height = FLAG_H;
      gfx.addChild(sprite);
    } else {
      // fallback — прямоугольник
      const fallback = new Graphics();
      const color = FLAG_COLORS[team];
      fallback.rect(0, 0, 4, FLAG_H).fill({ color: 0x888888 });
      fallback.rect(4, 0, FLAG_W - 4, FLAG_H * 0.6).fill({ color });
      gfx.addChild(fallback);
    }
  }

  bounds(flag: FlagState): AABB {
    return { x: flag.x, y: flag.y, w: FLAG_W, h: FLAG_H };
  }

  baseBounds(flag: FlagState): AABB {
    return { x: flag.baseX - 32, y: flag.baseY - 48, w: FLAG_W + 64, h: FLAG_H + 96 };
  }

  // Возвращает флаг который несёт игрок (или null)
  carriedFlag(carrier: 'player'): FlagState | null {
    return this.flags.find(f => f.carriedBy === carrier) ?? null;
  }

  // Флаги которые игрок НЕ несёт
  freeFlags(): FlagState[] {
    return this.flags.filter(f => f.carriedBy === null);
  }

  // Обновление: проверяем подбор и сдачу флага
  update(
    playerBounds: AABB,
    playerTeam: 'red' | 'blue',
    playerX: number,
    playerY: number,
  ): { scored: boolean; team: 'red' | 'blue' } | null {
    const carrying = this.carriedFlag('player');

    // Проверяем подбор свободных флагов
    if (!carrying) {
      for (const flag of this.freeFlags()) {
        if (aabbOverlap(playerBounds, this.bounds(flag))) {
          // нельзя подбирать свой флаг на базе
          if (flag.team === playerTeam && flag.onBase) continue;
          flag.pickupX = flag.x;
          flag.pickupY = flag.y;
          flag.carriedBy = 'player';
          flag.onBase = false;
          return null;
        }
      }
    }

    // Проверяем сдачу флага
    if (carrying) {
      if (carrying.team !== playerTeam) {
        // Несём вражеский флаг — проверяем свою базу
        const myBase = this.flags.find(f => f.team === playerTeam)!;
        if (myBase.onBase && aabbOverlap(playerBounds, this.baseBounds(myBase))) {
          // Очко!
          carrying.carriedBy = null;
          carrying.onBase = true;
          carrying.x = carrying.baseX;
          carrying.y = carrying.baseY;
          carrying.gfx.x = carrying.x;
          carrying.gfx.y = carrying.y;
          if (playerTeam === 'red') this.scoreRed++;
          else this.scoreBlue++;
          return { scored: true, team: playerTeam };
        }
      } else {
        // Несём свой флаг — возвращаем на базу
        if (aabbOverlap(playerBounds, this.baseBounds(carrying))) {
          carrying.carriedBy = null;
          carrying.onBase = true;
          carrying.x = carrying.baseX;
          carrying.y = carrying.baseY;
          carrying.gfx.x = carrying.x;
          carrying.gfx.y = carrying.y;
        }
      }
    }

    return null;
  }

  // При смерти носителя:
  // - вражеский флаг → возвращается на базу врага
  // - свой флаг → возвращается на место где был подобран
  dropFlag(carrier: 'player', carrierTeam: 'red' | 'blue', x: number, y: number, bySpike = false): void {
    const flag = this.carriedFlag(carrier);
    if (!flag) return;
    flag.carriedBy = null;
    if (flag.team !== carrierTeam) {
      // вражеский флаг: шипы → на базу врага, пуля → падает на месте
      if (bySpike) {
        flag.x = flag.baseX;
        flag.y = flag.baseY;
        flag.onBase = true;
      } else {
        flag.x = x;
        flag.y = y;
        flag.onBase = false;
      }
    } else {
      // свой флаг → на место подбора
      flag.x = flag.pickupX;
      flag.y = flag.pickupY;
      flag.onBase = false;
    }
    flag.gfx.x = flag.x;
    flag.gfx.y = flag.y;
  }

  // Синхронизация позиции флага с носителем
  syncCarried(carrier: 'player', x: number, y: number): void {
    const flag = this.carriedFlag(carrier);
    if (!flag) return;
    flag.x = x;  // центр персонажа без смещения влево
    flag.y = y - FLAG_H + 8;  // чуть выше верха hitbox
    flag.gfx.x = flag.x;
    flag.gfx.y = flag.y;
  }

  hasWinner(): 'red' | 'blue' | null {
    if (this.scoreRed >= this.scoreToWin) return 'red';
    if (this.scoreBlue >= this.scoreToWin) return 'blue';
    return null;
  }

  destroy(): void {
    for (const f of this.flags) f.gfx.destroy();
    this.flags = [];
  }
}
